import { useEffect, useRef, useState } from "react";
import { Navigate } from "react-router-dom";
import { Banner, Button, Field, Page, Spinner } from "../../components";
import { AuthLayout } from "./AuthLayout";
import { QrCode } from "./QrCode";
import { RecoveryCodesPanel } from "./RecoveryCodesPanel";
import { ApiError, describeApiError } from "../../api/errors";
import { cleanCode, confirmMfaEnrollment, sendMfaEnrollmentCode, startMfaEnrollment } from "../../api/mfa";
import { useSession } from "../../api/session";
import type { MfaEnrollment } from "../../api/types";
import { t } from "../../i18n/t";
import { useFinishSignIn } from "./mfaFlow";

/**
 * xmg-sec-mfa (S3a): `/auth/mfa/setup`, the forced enrolment. Every account must have a second factor before any
 * other screen works - new accounts and existing ones alike, on their next sign-in. Three steps on one screen:
 * scan the QR code (or type the key by hand), confirm with the first code, then keep the ten recovery codes,
 * which are shown exactly once.
 *
 * xmg-sec-mfa-takeover: before any of that, the account proves it controls its mailbox, not only its password -
 * a set-up code is emailed and typed back here (same browser). The screen first tries to start without one: a
 * session that already proved the code (a reload mid-set-up) goes straight to the QR code; any other gets
 * `email_code_required` and the email step.
 */
export function MfaSetupScreen(): JSX.Element {
  const { status, mfa, session, markMfaVerified } = useSession();
  const { target, finish } = useFinishSignIn();
  const [enrollment, setEnrollment] = useState<MfaEnrollment | null>(null);
  const [startError, setStartError] = useState<string | null>(null);
  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [codes, setCodes] = useState<string[] | null>(null);
  const [needsEmail, setNeedsEmail] = useState(false);
  const [sentTo, setSentTo] = useState<string | null>(null);
  const [emailCode, setEmailCode] = useState("");
  const [emailError, setEmailError] = useState<string | null>(null);
  const started = useRef(false);

  const needsSetup = status === "authenticated" && mfa === "enrollment_required";

  useEffect(() => {
    if (!needsSetup || started.current) return;
    started.current = true;
    startMfaEnrollment(null)
      .then(setEnrollment)
      .catch((caught: unknown) => {
        if (
          caught instanceof ApiError &&
          caught.is("mfa_state_conflict") &&
          caught.details.reason === "email_code_required"
        ) {
          setNeedsEmail(true);
        } else {
          setStartError(describeApiError(caught, t("error.unexpected")));
        }
      });
  }, [needsSetup]);

  if (status === "loading") return <Spinner />;
  if (status === "anonymous") return <Navigate to="/auth/login" replace state={{ from: target.from }} />;
  if (codes !== null) {
    return (
      <AuthLayout>
        <Page narrow eyebrow={t("mfa.eyebrow")} title={t("mfa.codes.title")}>
          <RecoveryCodesPanel
            codes={codes}
            account={enrollment?.account ?? session?.operator ?? ""}
            continueLabel={t("mfa.setup.finish")}
            onContinue={() => {
              markMfaVerified();
              finish();
            }}
          />
        </Page>
      </AuthLayout>
    );
  }
  if (mfa === "required") return <Navigate to="/auth/mfa" replace state={target} />;
  if (mfa !== "enrollment_required") return <Navigate to={target.from} replace />;

  async function onSendEmail(): Promise<void> {
    setBusy(true);
    setEmailError(null);
    try {
      const sent = await sendMfaEnrollmentCode();
      setSentTo(sent.sent_to);
      setEmailCode("");
    } catch (caught) {
      setEmailError(
        caught instanceof ApiError && caught.is("rate_limited")
          ? t("mfa.setup.email_throttled")
          : describeApiError(caught, t("error.unexpected")),
      );
    } finally {
      setBusy(false);
    }
  }

  async function onEmailCode(event: React.FormEvent): Promise<void> {
    event.preventDefault();
    setBusy(true);
    setEmailError(null);
    try {
      setEnrollment(await startMfaEnrollment(emailCode.trim()));
      setNeedsEmail(false);
    } catch (caught) {
      if (caught instanceof ApiError && caught.is("mfa_invalid_code")) {
        setEmailError(t("mfa.setup.email_invalid"));
      } else if (caught instanceof ApiError && caught.is("rate_limited")) {
        const seconds = caught.details.retry_after_seconds;
        setEmailError(t("mfa.challenge.throttled", { seconds: typeof seconds === "number" ? seconds : 60 }));
      } else {
        setEmailError(describeApiError(caught, t("error.unexpected")));
      }
    } finally {
      setBusy(false);
    }
  }

  async function onConfirm(event: React.FormEvent): Promise<void> {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const issued = await confirmMfaEnrollment(cleanCode(code));
      setCodes(issued.recovery_codes);
    } catch (caught) {
      if (caught instanceof ApiError && caught.is("mfa_invalid_code")) {
        setError(t("mfa.setup.invalid"));
      } else if (caught instanceof ApiError && caught.is("rate_limited")) {
        const seconds = caught.details.retry_after_seconds;
        setError(t("mfa.challenge.throttled", { seconds: typeof seconds === "number" ? seconds : 60 }));
      } else {
        setError(describeApiError(caught, t("error.unexpected")));
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <AuthLayout>
      <Page narrow eyebrow={t("mfa.eyebrow")} title={t("mfa.setup.title")}>
        <p>{t("mfa.setup.intro")}</p>
        {startError !== null ? <Banner tone="block">{startError}</Banner> : null}
        {enrollment === null && startError === null && !needsEmail ? <Spinner /> : null}
        {enrollment === null && needsEmail ? (
          <div className="authform">
            <p>{t("mfa.setup.email_intro")}</p>
            {sentTo === null ? (
              <>
                {emailError !== null ? <Banner tone="block">{emailError}</Banner> : null}
                <Button
                  type="button"
                  variant="primary"
                  className="button--auth"
                  disabled={busy}
                  onClick={() => void onSendEmail()}
                >
                  {t("mfa.setup.email_send")}
                </Button>
              </>
            ) : (
              <form onSubmit={(event) => void onEmailCode(event)}>
                <p>{t("mfa.setup.email_sent", { address: sentTo })}</p>
                <Field label={t("mfa.setup.email_code_label")} error={emailError}>
                  {({ id, describedBy }) => (
                    <input
                      id={id}
                      aria-describedby={describedBy}
                      autoComplete="one-time-code"
                      autoCapitalize="characters"
                      className="mono"
                      value={emailCode}
                      onChange={(event) => setEmailCode(event.target.value)}
                    />
                  )}
                </Field>
                <Button
                  type="submit"
                  variant="primary"
                  className="button--auth"
                  disabled={busy || emailCode.trim() === ""}
                >
                  {t("mfa.setup.email_submit")}
                </Button>
                <Button type="button" disabled={busy} onClick={() => void onSendEmail()}>
                  {t("mfa.setup.email_resend")}
                </Button>
              </form>
            )}
          </div>
        ) : null}
        {enrollment !== null ? (
          <form onSubmit={(event) => void onConfirm(event)} className="authform">
            <ol className="mfasteps">
              <li>
                <p>{t("mfa.setup.step_scan")}</p>
                <QrCode matrix={enrollment.qr_matrix} label={t("mfa.setup.qr_label")} />
              </li>
              <li>
                <p>{t("mfa.setup.step_manual")}</p>
                <p className="mono mfasecret" aria-label={t("mfa.setup.secret_label")}>
                  {enrollment.secret.replace(/(.{4})/g, "$1 ").trim()}
                </p>
              </li>
              <li>
                <Field label={t("mfa.setup.code_label")} error={error}>
                  {({ id, describedBy }) => (
                    <input
                      id={id}
                      aria-describedby={describedBy}
                      inputMode="numeric"
                      autoComplete="one-time-code"
                      className="mono"
                      value={code}
                      onChange={(event) => setCode(event.target.value)}
                    />
                  )}
                </Field>
              </li>
            </ol>
            <Button
              type="submit"
              variant="primary"
              className="button--auth"
              disabled={busy || code.trim() === ""}
            >
              {t("mfa.setup.submit")}
            </Button>
          </form>
        ) : null}
      </Page>
    </AuthLayout>
  );
}
